import { AppWebsocketConnection } from '@/shared/features/websockets/classes/AppWebsocketConnection';
import {
  WSCloseCodes,
  WSConnectionClosedEventDetail,
  WSConnectionErrorEventDetail,
  WSConnectionOpenEventDetail,
  WSTokenExpiredEventDetail,
} from '@/shared/features/websockets/types/websockets';
import { msg } from '@/shared/helpers/msg';
import { WS_CONNECTION_CLOSE, WS_CONNECTION_ERROR, WS_CONNECTION_OPEN, WS_TOKEN_EXPIRED } from '@/shared/features/websockets/events';

// DEBUG!!!!
// let date = 0;
//
// const intervals: number[] = [];

export class AppWebSocketManager {
  protected connections: Record<string, AppWebsocketConnection> = {};
  protected token = '';
  protected statuses: Record<
    string,
    {
      ok: boolean;
      retrying: boolean;
    }
  > = {};
  public init() {
    this.setupNetworkEventListeners();
    this.setupCloseErrorEventListeners();
  }

  public addToken(token: string) {
    if (token === this.token) return;
    this.token = token;
    this.closeAllConnections(WSCloseCodes.Normal);
    this.initAllConnections();
  }

  public removeToken() {
    this.token = '';
    this.closeAllConnections(WSCloseCodes.Normal);
  }

  public addConnection(path: string, create?: boolean) {
    if (path in this.connections) return this.connections[path];

    const conn = new AppWebsocketConnection(path);

    this.connections[path] = conn;

    if (create) {
      conn.create({ token: this.token });
    }
  }

  public initAllConnections(notify?: boolean) {
    Object.values(this.connections).forEach((conn) => {
      conn.create({
        token: this.token,
        notify,
      });
    });
  }

  public closeAllConnections(code: WSCloseCodes, reason?: string) {
    Object.values(this.connections).forEach((conn) => {
      conn.close(code, reason);
    });
  }

  public getConnection(path: string) {
    return this.connections[path];
  }

  protected requestTokenUpdate() {
    const wsTokenExpiredEvent = new CustomEvent<WSTokenExpiredEventDetail>(WS_TOKEN_EXPIRED, {
      bubbles: true,
      detail: {
        token: this.token,
      },
    });

    document.dispatchEvent(wsTokenExpiredEvent);
  }

  // always reconnect
  protected async setupCloseErrorEventListeners() {
    const handleCloseAndError = (({ detail }: CustomEvent<WSConnectionClosedEventDetail | WSConnectionErrorEventDetail>) => {
      // if token is expired
      if ('data' in detail && 'code' in detail.data && detail.data.code === 1008) {
        this.requestTokenUpdate();
        this.removeToken();
        return;
      }

      const { path } = detail;
      const handleTimeout = () =>
        setTimeout(() => {
          // checking for token
          if (!this.token) return;
          // stop if status is ok
          if (this.statuses[path].ok) return;

          this.getConnection(path).create({ token: this.token });
          this.statuses[path].retrying = false;
        }, 1000);
      if (this.statuses[path]?.retrying) return;
      this.statuses[path] = {
        ok: false,
        retrying: true,
      };

      handleTimeout();
    }) as EventListener;

    const handleOpen = (({ detail }: CustomEvent<WSConnectionOpenEventDetail>) => {
      if (this.statuses[detail.path]) {
        this.statuses[detail.path].ok = true;
      } else {
        this.statuses[detail.path] = {
          ok: true,
          retrying: false,
        };
      }
    }) as EventListener;

    document.addEventListener(WS_CONNECTION_CLOSE, handleCloseAndError);
    document.addEventListener(WS_CONNECTION_ERROR, handleCloseAndError);
    document.addEventListener(WS_CONNECTION_OPEN, handleOpen);
  }

  protected setupNetworkEventListeners() {
    window.addEventListener('offline', () => {
      Object.values(this.connections).forEach((conn) => {
        conn.close(WSCloseCodes.Offline, 'Now you are offline');
      });
    });
    window.addEventListener('online', () => {
      if (!this.token) return;
      msg.open({ content: 'Reconnecting...', key: 'ws', type: 'loading' });
      Object.values(this.connections).forEach((conn) => {
        conn.create({ token: this.token, notify: true });
      });
    });
  }
}
